/**
 * GetCollectionRequestParamsTask - трансформує параметри списку в query параметри для API
 * 
 * Обробляє:
 * - Пагінацію (page, itemsPerPage)
 * - Сортування (order[field])
 * - Фільтри (різні типи: дати, масиви, об'єкти, числа)
 */

import dayjs from 'dayjs'

export interface CollectionRequestParams {
    filter?: Record<string, any>
    pagination?: {
        current: number
        pageSize: number
    }
    sorter?: {
        key?: string
        order?: 'ascend' | 'descend'
    }
}

/**
 * Трансформує параметри в query для API запиту
 */
export const GetCollectionRequestParamsTask = (
    object: CollectionRequestParams = {},
    crudContext?: any
): Record<string, any> => {
    const filter = typeof object?.filter === 'object' ? object.filter : {}
    const pagination = typeof object?.pagination === 'object' ? object.pagination : { current: 1, pageSize: 10 }
    const sorter = typeof object?.sorter === 'object' ? object.sorter : {}

    const params: Record<string, any> = {
        page: pagination.current,
        itemsPerPage: pagination.pageSize,
    }

    // Додаємо сортування
    if (sorter?.key && sorter?.order) {
        params['order[' + sorter.key + ']'] = sorter.order
    }

    const forRemove: string[] = []

    // Обробляємо фільтри
    Object.keys(filter).forEach((key) => {
        let field = crudContext?.formContext?.fields?.get(key)

        if (!field) {
            field = crudContext?.filterFormContext?.fields?.get(key)
        }

        if (filter[key] !== undefined && (filter[key] === false || filter[key])) {
            // Діапазон дат
            if (
                Array.isArray(filter[key]) &&
                dayjs.isDayjs(filter[key][0]) &&
                dayjs.isDayjs(filter[key][1])
            ) {
                const dates = filter[key]
                params[`${key}[after]`] = dates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss')
                params[`${key}[before]`] = dates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
            }
            // Масиви
            else if (Array.isArray(filter[key])) {
                filter[key].forEach((item: any, index: number) => {
                    if (item['@id'] !== undefined) {
                        params[`${key}[${index}]`] = item['@id']
                    } else {
                        params[`${key}[${index}]`] = item
                    }
                })
            }
            // Об'єкти з responseValue
            else if (
                typeof filter[key] === 'object' &&
                field &&
                typeof field.props?.responseValue === 'function'
            ) {
                params[key] = field.props.responseValue(filter[key])
            }
            // Об'єкти з IRI
            else if (typeof filter[key] === 'object' && filter[key]['@id'] !== undefined) {
                params[key] = filter[key]['@id']
            }
            // Числові поля з префіксом before_
            else if (
                field &&
                field.type === 'number' &&
                filter[`before_${key}`] !== undefined &&
                filter[`before_${key}`] !== 0
            ) {
                params[`${key}[${filter[`before_${key}`]}]`] = filter[key]
                forRemove.push(`before_${key}`)
            }
            // Звичайні значення
            else {
                params[key] = filter[key]
            }
        }
    })

    // Видаляємо допоміжні параметри
    forRemove.forEach((key) => {
        if (params[key] !== undefined) {
            delete params[key]
        }
    })

    return params
}
